import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "../components/ui/table";
import Button from "../components/ui/button/Button";
import { Plus, Building2, ChevronRight, Loader2, AlertCircle, Mail, Phone, Globe } from "lucide-react";
import brandService from "../api/brandService"; 

export default function InternalEntitiesList({ isDark, activeFilters, onDataLoaded }) {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const hasFetched = useRef(false);

  // Dynamic Brand Colors
  const brandBlue = isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]';
  const secondaryTextColor = isDark ? 'text-slate-300' : 'text-slate-600';

  const fetchBrands = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await brandService.getAllBrands(); 
      if (Array.isArray(res)) {
        setCompanies(res);
        if (onDataLoaded) onDataLoaded(res);
      } else {
        setCompanies([]);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch entities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasFetched.current) {
      fetchBrands();
      hasFetched.current = true;
    }
  }, []);

  const filteredAndSorted = useMemo(() => {
    const filtered = companies.filter((c) => {
      const sidebarBrandNames = activeFilters?.brand_name || [];
      const matchesSidebarName = sidebarBrandNames.length === 0 || sidebarBrandNames.includes(c.brand_name);

      const statusFilters = activeFilters?.is_active || [];
      let matchesStatus = true;
      if (statusFilters.length > 0) {
        const itemStatus = c.is_active == 1 ? "Active" : "Inactive";
        matchesStatus = statusFilters.includes(itemStatus);
      }

      return matchesSidebarName && matchesStatus;
    });

    return [...filtered].sort((a, b) => {
      if (a.is_active !== b.is_active) {
        return b.is_active - a.is_active;
      }
      return a.brand_name.localeCompare(b.brand_name);
    });
  }, [companies, activeFilters]);

  return (
    <div className={`p-1 sm:p-6 space-y-6 min-h-screen transition-colors duration-300 ${isDark ? 'bg-slate-800/0' : 'bg-slate-100/0'}`}>
      
      {/* Header Container */}
      <div className="relative flex flex-col items-center justify-center min-h-[120px] sm:min-h-[100px] sm:-mt-8 max-w-7xl mx-auto w-full px-4">
        <div className="text-center">
          <h2 className={`text-2xl font-brand-heading font-bold uppercase tracking-tighter leading-tight ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
            <span className="text-orange-600">MANAGE </span><span className={isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]'}>Brands</span>
          </h2>
          
          <div className="flex items-center justify-center gap-3 mt-0">
            <div className={`h-px w-4 ${isDark ? 'bg-slate-400' : 'bg-slate-400'}`} />
            <p className={`text-[13px] uppercase tracking-[0.1em] ${secondaryTextColor}`}>
              Brands & Services
            </p>
            <div className={`h-px w-4 ${isDark ? 'bg-slate-400' : 'bg-slate-400'}`} />
          </div>
        </div>
        
        <div className="mt-4 sm:mt-0 sm:absolute sm:right-4 sm:top-1/2 sm:-translate-y-1/2 flex items-center">
          <Button 
            isDark={isDark}
            onClick={() => navigate('/brands/new')} 
            className={`
              group flex items-center px-5 py-2.5 rounded-xl font-bold uppercase tracking-wider text-xs transition-all border shadow-sm hover:shadow-md active:scale-95 whitespace-nowrap
              ${isDark ? 'bg-slate-900/50 border-slate-700 hover:bg-slate-800' : 'bg-white border-slate-200 hover:bg-slate-50'}
            `}
          >
            <Plus size={18} strokeWidth={3} className="text-orange-600 transition-transform duration-500 ease-out group-hover:rotate-90" /> 
            <span className={`ml-2 ${isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]'}`}>New Brand</span>
          </Button>
        </div>
      </div>

      {error && (
        <div className="max-w-7xl mx-auto w-full p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-500 text-sm animate-in fade-in slide-in-from-top-2">
          <AlertCircle size={18} /> Error: {error}
        </div>
      )}

      {/* Table Container */}
      <div className={`rounded-2xl border shadow-sm overflow-hidden max-w-7xl mx-auto w-full transition-all ${
        isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'
      }`}>
        <div className="overflow-x-auto w-full">
          {loading && companies.length === 0 ? (
            <div className="py-20 flex flex-col items-center justify-center gap-4">
              <Loader2 className={`animate-spin ${brandBlue}`} size={32} />
              <span className={`uppercase tracking-[0.2em] text-[10px] font-bold ${secondaryTextColor}`}>Initialising Registry...</span>
            </div>
          ) : (
            <Table className="min-w-[800px] w-full text-left">
              <TableHeader className={isDark ? 'bg-slate-950/50' : 'bg-slate-50/50'}>
                <TableRow className={isDark ? 'border-slate-800' : 'border-slate-100'}>
                  <TableCell isHeader className={`px-3 sm:px-6 py-4 text-[11px] font-black tracking-widest ${secondaryTextColor}`}>Brand</TableCell>
                  <TableCell isHeader className={`px-3 sm:px-6 py-4 text-[11px] font-black tracking-widest ${secondaryTextColor}`}>Status</TableCell>
                  <TableCell isHeader className="px-3 sm:px-6 py-4 text-right"></TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSorted.length > 0 ? (
                  filteredAndSorted.map((company) => (
                    <TableRow 
                      key={company.id} 
                      className={`cursor-pointer transition-all group border-b last:border-none ${
                        isDark ? 'hover:bg-slate-800/50 border-slate-800' : 'hover:bg-blue-50/30 border-slate-50'
                      }`}
                      onClick={() => navigate(`/brands/${company.id}`)}
                    >
                      <TableCell className="px-3 sm:px-6 py-5">
                        <div className="flex items-center gap-3 sm:gap-4">
                          {/* Building Icon Logic: Colors by Status */}
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center border transition-all flex-shrink-0 ${
                            isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'
                          } ${
                            company.is_active == 1 
                              ? (isDark ? 'group-hover:border-[#5A9AFF]/40' : 'group-hover:border-[#2D4D94]/30')
                              : 'group-hover:border-orange-400/40'
                          }`}>
                            <Building2 
                              size={20} 
                              className={`sm:size-[24px] transition-colors ${
                                company.is_active == 1 ? brandBlue : 'text-orange-400'
                              }`} 
                            />
                          </div>
                          <div className="space-y-1 min-w-0">
                            <div className={` text-xs sm:text-sm truncate transition-colors ${
                              isDark ? 'text-slate-100' : 'text-slate-900'
                            } ${
                              company.is_active == 1 
                                ? (isDark ? 'group-hover:text-[#5A9AFF]' : 'group-hover:text-[#2D4D94]')
                                : 'group-hover:text-orange-400'
                            }`}>
                              {company.brand_name}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                              {company.email && (
                                <div className={`flex items-center gap-1.5 text-[10px] font-bold tracking-wider opacity-60 ${secondaryTextColor}`}>
                                  <Mail size={10} className={`${company.is_active == 1 ? brandBlue : 'text-orange-400'} flex-shrink-0`} /> 
                                  <span className="truncate max-w-[120px] sm:max-w-none">{company.email}</span>
                                </div>
                              )}
                              {company.phone_number && (
                                <div className={`flex items-center gap-1.5 text-[10px] font-bold tracking-wider opacity-60 ${secondaryTextColor}`}>
                                  <Phone size={10} className={`${company.is_active == 1 ? brandBlue : 'text-orange-400'} flex-shrink-0`} /> 
                                  {company.phone_number}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="px-3 sm:px-6 py-5">
                        <span className={`px-3 py-1.5 text-[11px] font-bold  border rounded-lg inline-flex items-center gap-1.5 transition-colors duration-300 ${
                          company.is_active == 1 
                            ? isDark 
                              ? 'text-[#5A9AFF] border-[#5A9AFF]/30 bg-[#5A9AFF]/10' 
                              : 'text-[#2D4D94] border-[#2D4D94]/20 bg-[#2D4D94]/5'
                            : 'text-orange-400 border-orange-400/20 bg-orange-400/5'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            company.is_active == 1 
                              ? (isDark ? 'bg-[#5A9AFF]' : 'bg-[#2D4D94]')
                              : 'bg-orange-400'
                          }`} />
                          {company.is_active == 1 ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell className="px-3 sm:px-6 py-5 text-right">
                        <div className={`flex justify-end items-center group-hover:translate-x-1 transition-all ${
                          isDark ? 'text-slate-700' : 'text-slate-300'
                        } ${
                          company.is_active == 1 
                            ? (isDark ? 'group-hover:text-[#5A9AFF]' : 'group-hover:text-[#2D4D94]')
                            : 'group-hover:text-orange-400'
                        }`}>
                          <ChevronRight size={20} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} className="py-24 text-center">
                      <div className="flex flex-col items-center gap-2 opacity-30">
                         <Building2 size={40} className={brandBlue} />
                         <p className={`text-[10px] font-black uppercase tracking-[0.3em] ${secondaryTextColor}`}>Registry Empty</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}